import ReactMarkdown from 'react-markdown'
import { ContentWrapper } from 'components/feature/Article/Article.styles'
import { MessageSection } from 'interfaces/AiChatBot'
import NextImage from 'components/elements/NextImage'
import { InlineMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { useRef } from 'react'
import useImpressionTracking from 'lib/hooks/useImpressionTracking'
import { AnalyticsEventAiChatVcpImpression } from 'lib/constants/AnalyticsEventType'
import { getSessionTrackingUuid } from 'lib/tracking/storage'
import styled from 'styled-components'

const InlineMathWrapper = styled.span`
    display: inline;
    max-width: 100%;
    vertical-align: middle;
    //    Force everything to fit
    overflow: hidden;

    /* Force all KaTeX elements to fit */
    .katex,
    .katex-html,
    .katex-mathml,
    .base,
    .strut,
    .mord {
        max-width: 100% !important;
        width: auto !important;
        overflow-wrap: break-word !important;
        white-space: normal !important;
    }

    /* Target specific KaTeX text nodes */
    .katex .mord.mathnormal {
        overflow-wrap: break-word !important;
    }
    .katex .base {
        max-width: 100% !important;
    }
    span.mfrac > span.vlist-t > span.vlist-r > span.vlist > span:nth-child(1) {
        position: relative;
        top: -39.5px !important;
    }
`

const VcpContent = ({ section, messageUuid }: { section: MessageSection; messageUuid?: string }) => {
    const vcpRef = useRef<HTMLDivElement>(null)
    const sessionUuid = getSessionTrackingUuid()

    useImpressionTracking(
        vcpRef,
        AnalyticsEventAiChatVcpImpression,
        {
            value: section.vcpUuid,
            aiChat: {
                sessionUuid: sessionUuid ?? '',
                messageUuid: messageUuid ?? '',
            },
        },
        !!sessionUuid && !!messageUuid
    )

    return (
        <div key={section.vcpUuid} ref={vcpRef}>
            {section.imageDescription && (
                <NextImage
                    src={section.url}
                    alt={section.imageDescription}
                    width={section.imageWidth || undefined}
                    quality={100}
                    height={section.imageHeight || undefined}
                    style={{
                        display: 'inline-block',
                        maxWidth: '680px',
                        objectFit: 'contain',
                        height: 'auto',
                    }}
                />
            )}
        </div>
    )
}

const displayMessageSection = (section: MessageSection, messageUuid?: string) => {
    switch (section.type) {
        case 'vcp':
            return <VcpContent section={section} messageUuid={messageUuid} />
        case 'markdown':
        case 'text':
            return (
                <ContentWrapper key={section.text} isDarkTheme>
                    <ReactMarkdown
                        components={{
                            p: ({ children }) => <p>{parseLatexInText(children)}</p>,
                            li: ({ children }) => <li>{parseLatexInText(children)}</li>,
                        }}
                    >
                        {section.text || ''}
                    </ReactMarkdown>
                </ContentWrapper>
            )
        default:
            return null
    }
}

export const parseLatexInText = (children: React.ReactNode): React.ReactNode => {
    if (!children) return null

    const childrenArray = Array.isArray(children) ? children : [children]

    const processNode = (node: React.ReactNode): string => {
        if (typeof node === 'string') return node
        if (Array.isArray(node)) return node.map(processNode).join('')
        if (typeof node === 'object' && node !== null) {
            if ('props' in node) {
                if (typeof node.props.children === 'string') return node.props.children
                if (Array.isArray(node.props.children)) return node.props.children.map(processNode).join('')
            }
        }
        return ''
    }

    const text = childrenArray.map(processNode).join('')
    const parts = text.split(/(\$[^$]+\$|\[[^\]]+\])/g)

    return parts.map((part, index) => {
        if (part.startsWith('$') && part.endsWith('$')) {
            const latex = part.slice(1, -1)
            return (
                <InlineMathWrapper key={index}>
                    <InlineMath math={latex} />
                </InlineMathWrapper>
            )
        }
        return part
    })
}

export const textBasedOnMessage = (message: string, messageSections?: MessageSection[], messageUuid?: string) => {
    if (messageSections?.length) {
        return <div>{messageSections.map((section) => displayMessageSection(section, messageUuid))}</div>
    } else {
        return (
            <ContentWrapper isDarkTheme>
                <ReactMarkdown
                    components={{
                        p: ({ children }) => <p>{parseLatexInText(children)}</p>,
                        li: ({ children }) => <li>{parseLatexInText(children)}</li>,
                    }}
                >
                    {message}
                </ReactMarkdown>
            </ContentWrapper>
        )
    }
}
